import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'

import { Platform } from '@angular/cdk/platform'
import { DateAdapter } from '@angular/material/core'
import { MatDatepicker } from '@angular/material/datepicker'

import { KirjanpitajaService } from '../_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from '../_angular/service/kirjautunut-kayttaja.service'

import { DateService } from '@lemon/core/service/date.service'
import { LemonKuukausiDateAdapter } from 'app/_jaettu-angular/_material/LemonKuukausiDateAdapter'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { KirjanpitajanNimitiedot, KirjanpitajanTiimi } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import {
  JatkuvanLaskutuksenLahdeTieto,
  KirjanpitajanJatkuvanLaskutuksenKehitysYhteensa,
  KirjanpitajanMyyntiyhteenvetoYhteensa,
  KirjanpitajanTilastotiedotYhteensa,
  MyyntiYhteenvetoLahdeTieto,
  MyyntiYhteenvetoLahdeTietoNimitiedoilla,
  TiimaritJatkuvanLaskutuksenKehitysKentta,
  TiimaritMyyntiyhteenvetoKentta,
  TiimaritRaporttiTyyppi,
  TiimaritTilastotKentta,
  TiimaritTilastotPyynto,
  TiimaritTilastotVastaus,
  TilastotLahdeTieto,
  TilastotLahdeTietoNimitiedoilla,
} from 'app/_jaettu-lemonator/model/tilastot'
import { KirjanpitajanTiimiService } from '../_jaettu-lemonator/service/kirjanpitajan-tiimi.service'

import { combineLatest, Observable, Subject } from 'rxjs'
import { map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'

import { CurrencyService } from '@lemon/core/service/currency.service'
import { AnnaAsiakkaidenNimitiedotPyynto, AnnaAsiakkaidenNimitiedotVastaus, AsiakasTagi, AsiakkaanNimitiedot } from 'app/_jaettu-lemonator/model/asiakas'
import { YllapitoUriService } from 'app/_jaettu-lemonator/service/yllapito-uri.service'

interface TiimaritLomake {
  tiimi: KirjanpitajanTiimi['avain'] | null | ''
  kuukausi: Date | null
  tagi: string | null
}

interface TilastotTablenRivi {
  bold: boolean
  cols: TilastotSolunData[]
  nimi?: string
}

interface TilastotSolunData {
  arvo: string
  kirjanpitajaAvain?: string
  kenttaMyyntiyhteenveto?: TiimaritMyyntiyhteenvetoKentta
  kenttaJatkuvaLaskutus?: TiimaritJatkuvanLaskutuksenKehitysKentta
  kenttaTilastot?: TiimaritTilastotKentta
}

interface MyyntiyhteenvetoTila {
  valittuSolu: TilastotSolunData | null
  lahteet: MyyntiYhteenvetoLahdeTietoNimitiedoilla[]
  lahteetYhteissumma: number
  lataaLahteet: boolean
  lahteetEiTasmaa: boolean
}

interface JatkuvanLaskutuksenKehitysTila {
  valittuSolu: TilastotSolunData | null
  lahteet: JatkuvanLaskutuksenLahdeTieto[]
  lahteetYhteissumma: number
  lataaLahteet: boolean
  lahteetEiTasmaa: boolean
}

interface TilastotTila {
  valittuSolu: TilastotSolunData | null
  lahteet: TilastotLahdeTietoNimitiedoilla[]
  lahteetYhteissumma: number
  lataaLahteet: boolean
  lahteetEiTasmaa: boolean
}

@Component({
  templateUrl: './tilastot-tiimarit.component.html',
  styleUrls: ['./tilastot-tiimarit.component.css'],
  providers: [{ provide: DateAdapter, useClass: LemonKuukausiDateAdapter, deps: [ErrorHandler, DateService, Platform, LemonTranslationService] }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TilastotTiimaritComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  lomake: FieldTree<TiimaritLomake>

  tiimitObservable: Observable<KirjanpitajanTiimi[]>
  kaikkiTagitObservable: Observable<AsiakasTagi[]>

  myyntiyhteenvetoObservable: Observable<TilastotTablenRivi[]>
  readonly myyntiyhteenvetoTableLataa = signal(true)

  jatkuvanLaskutuksenKehitysObservable: Observable<TilastotTablenRivi[]>
  readonly jatkuvanLaskutuksenKehitysTableLataa = signal(true)

  tilastotObservable: Observable<TilastotTablenRivi[]>
  readonly tilastotTableLataa = signal(true)

  private readonly _myyntiyhteenvetoTila = signal<MyyntiyhteenvetoTila>(this._luoTyhjaMyyntiyhteenvetoTila())
  private readonly _jatkuvanLaskutuksenKehitysTila = signal<JatkuvanLaskutuksenKehitysTila>(this._luoTyhjaJatkuvanLaskutuksenKehitysTila())
  private readonly _tilastotTila = signal<TilastotTila>(this._luoTyhjaTilastotTila())

  get myyntiyhteenvetoTila() {
    return this._myyntiyhteenvetoTila()
  }
  get jatkuvanLaskutuksenKehitysTila() {
    return this._jatkuvanLaskutuksenKehitysTila()
  }
  get tilastotTila() {
    return this._tilastotTila()
  }

  kirjanpitajienNimitiedotMap: Map<string, KirjanpitajanNimitiedot> | null = null
  asiakkaatMapPromise: Promise<{ [avain: string]: AsiakkaanNimitiedot }>

  constructor(
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _kirjanpitajanTiimiService: KirjanpitajanTiimiService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _currencyService: CurrencyService,
    private _dateService: DateService,
    private _lemonatorFirestore: FirebaseLemonator,
    private _yllapitoUriService: YllapitoUriService,
  ) {}

  ngOnInit() {
    const edellinenKuukausi = this._dateService.lisaaKuukausia(new Date(), -1)

    this.lomake = form(signal<TiimaritLomake>({ tiimi: '', kuukausi: edellinenKuukausi, tagi: null }), { injector: this._injector })

    this.kaikkiTagitObservable = this._lemonatorFirestore
      .firestoreCollection<AsiakasTagi>(this._yllapitoUriService.annaAsiakasTagitCollectionUri())
      .listen()
      .pipe(
        map(tagit => (tagit || []).sort((a, b) => a.nimi.localeCompare(b.nimi))),
        lemonShare(),
      )

    this.asiakkaatMapPromise = this._lemonatorFirestore
      .functionsCall<AnnaAsiakkaidenNimitiedotPyynto, AnnaAsiakkaidenNimitiedotVastaus>('annaAsiakkaidenNimitiedot', {}, { timeout: 540 * 1000 })
      .then(asiakkaatVastaus => {
        const m: { [avain: string]: AsiakkaanNimitiedot } = {}
        if (asiakkaatVastaus?.nimitiedot) {
          for (const a of asiakkaatVastaus.nimitiedot) {
            m[a.avain] = a
          }
        }
        return m
      })
      .catch(err => {
        console.error('Virhe haettaessa asiakkaiden nimitietoja:', err)
        return {}
      })

    const kaikkiTiimitObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(
      switchMap(k => {
        return this._kirjanpitajanTiimiService.annaKaikkiTiimit()
      }),
    )

    this.tiimitObservable = kaikkiTiimitObservable

    this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(nimitiedotMap => {
      this.kirjanpitajienNimitiedotMap = nimitiedotMap
    })

    const tiimaritTilastotObservable: Observable<TiimaritTilastotVastaus> = arvonMuutokset(this.lomake, { injector: this._injector }).pipe(
      startWith(this.lomake().value()),
      tap(() => {
        this.myyntiyhteenvetoTableLataa.set(true)
        this.jatkuvanLaskutuksenKehitysTableLataa.set(true)
        this.tilastotTableLataa.set(true)
        this._myyntiyhteenvetoTila.set(this._luoTyhjaMyyntiyhteenvetoTila())
        this._jatkuvanLaskutuksenKehitysTila.set(this._luoTyhjaJatkuvanLaskutuksenKehitysTila())
        this._tilastotTila.set(this._luoTyhjaTilastotTila())
      }),
      switchMap(async formValue => {
        if (!formValue.kuukausi || formValue.tiimi === '') {
          return Promise.resolve({ myyntiyhteenveto: [], jatkuvanLaskutuksenKehitys: [], tilastot: [] })
        }
        const kuukausi = this._dateService.dateToLocalMonth(formValue.kuukausi)

        const payload: TiimaritTilastotPyynto = {
          tiimi: formValue.tiimi,
          aikavali: { start: kuukausi, end: kuukausi },
          rajoitettuTagiinAvain: formValue.tagi ?? null,
        }
        const vastaus = await this._lemonatorFirestore.functionsCall<TiimaritTilastotPyynto, TiimaritTilastotVastaus>('tiimaritTilastot', payload)
        return vastaus
      }),
      lemonShare(),
    )

    const tilastotJaNimitiedotObservable = combineLatest([tiimaritTilastotObservable, this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable])

    this.myyntiyhteenvetoObservable = tilastotJaNimitiedotObservable.pipe(
      map(([vastaus, nimitiedotMap]) => {
        const rivit: TilastotTablenRivi[] = []
        const otsikkoRivi: TilastotTablenRivi = {
          cols: [
            { arvo: 'Kuukausittainen palvelumaksu' },
            { arvo: 'Jatkuvaluonteinen kertalaskutus' },
            { arvo: 'Kertalaskutus' },
            { arvo: 'Kertalaskutus yhteensä' },
            { arvo: 'Palvelulaskutus yhteensä' },
            { arvo: 'Palkkapalvelut' },
            { arvo: 'Ohjelmisto' },
            { arvo: 'Muu laskutus' },
            { arvo: 'Yhteensä' },
            { arvo: 'Hyvitykset' },
          ],
          nimi: 'Kirjanpitäjä',
          bold: true,
        }
        rivit.push(otsikkoRivi)

        const sortattuData = this.sorttaaKirjanpitajatNimenMukaan(vastaus.myyntiyhteenveto, nimitiedotMap)

        for (const data of sortattuData) {
          const nimi = this.muodostaKirjanpitajanNimi(data.kirjanpitaja, nimitiedotMap)

          const kirjanpitajaRivi: TilastotTablenRivi = {
            cols: [
              {
                arvo: this._currencyService.formatoiRaha(data.kuukausittainenPalvelumaksu, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.KUUKAUSITTAINEN_PALVELUMAKSU,
              },
              {
                arvo: this._currencyService.formatoiRaha(data.jatkuvaluonteinenKertalaskutus, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.JATKUVALUONTEINEN_KERTALASKUTUS,
              },
              { arvo: this._currencyService.formatoiRaha(data.kertalaskutus, 'EUR', 'fi'), kirjanpitajaAvain: data.kirjanpitaja, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.KERTALASKUTUS },
              {
                arvo: this._currencyService.formatoiRaha(data.kertalaskutusYhteensa, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.KERTALASKUTUS_YHTEENSA,
              },
              {
                arvo: this._currencyService.formatoiRaha(data.palvelulaskutusYhteensa, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.PALVELULASKUTUS_YHTEENSA,
              },
              {
                arvo: this._currencyService.formatoiRaha(data.palkkapalvelut, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.PALKKAPALVELUT,
              },
              { arvo: this._currencyService.formatoiRaha(data.ohjelmisto, 'EUR', 'fi'), kirjanpitajaAvain: data.kirjanpitaja, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.OHJELMISTO },
              { arvo: this._currencyService.formatoiRaha(data.muuLaskutus, 'EUR', 'fi'), kirjanpitajaAvain: data.kirjanpitaja, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.MUU_LASKUTUS },
              { arvo: this._currencyService.formatoiRaha(data.yhteensa, 'EUR', 'fi'), kirjanpitajaAvain: data.kirjanpitaja, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.YHTEENSA },
              { arvo: this._currencyService.formatoiRaha(data.hyvitykset, 'EUR', 'fi'), kirjanpitajaAvain: data.kirjanpitaja, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.HYVITYKSET },
            ],
            nimi,
            bold: false,
          }
          rivit.push(kirjanpitajaRivi)
        }

        if (vastaus.myyntiyhteenvetoYhteensa) {
          const yhteensaRivi = this._luoMyyntiyhteenvedonYhteensaRivi(vastaus.myyntiyhteenvetoYhteensa)
          rivit.push(yhteensaRivi)
        }

        return rivit
      }),
      tap(() => {
        this.myyntiyhteenvetoTableLataa.set(false)
      }),
    )

    this.jatkuvanLaskutuksenKehitysObservable = tilastotJaNimitiedotObservable.pipe(
      map(([vastaus, nimitiedotMap]) => {
        const naytaTagiSarake = vastaus.jatkuvanLaskutuksenKehitys.some(data => data.muutoksetTagirajauksessa !== undefined)
        const rivit: TilastotTablenRivi[] = []
        const otsikkoRivi: TilastotTablenRivi = {
          cols: [
            { arvo: 'Edellisen kuukauden loppusaldo' },
            { arvo: 'Päättyneet sopimukset' },
            { arvo: 'Muutokset pöytä- laatikoissa' },
            { arvo: 'Muutokset tauoissa' },
            { arvo: 'Hinnan- muutokset' },
            { arvo: 'Sisäiset siirrot' },
            ...(naytaTagiSarake ? [{ arvo: 'Muutokset tagi- rajauksessa' }] : []),
            { arvo: 'Alkaneet sopimukset' },
            { arvo: 'Jatkuva laskutus yhteensä' },
            { arvo: 'Muutos edelliseen kuukauteen' },
            { arvo: 'Poistuma yhteensä' },
            { arvo: 'Lisäys yhteensä' },
          ],
          nimi: 'Kirjanpitäjä',
          bold: true,
        }
        rivit.push(otsikkoRivi)

        const sortattuData = this.sorttaaKirjanpitajatNimenMukaan(vastaus.jatkuvanLaskutuksenKehitys, nimitiedotMap)

        for (const data of sortattuData) {
          const nimi = this.muodostaKirjanpitajanNimi(data.kirjanpitaja, nimitiedotMap)

          const kirjanpitajaRivi: TilastotTablenRivi = {
            cols: [
              {
                arvo: this._currencyService.formatoiRaha(data.edellinenKkLoppu, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.EDELLINEN_KK_LOPPU,
              },
              {
                arvo: this._currencyService.formatoiRaha(data.paattyneetSopimukset, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.PAATTYNEET_SOPIMUKSET,
              },
              {
                arvo: this._currencyService.formatoiRaha(data.muutoksetPoytalaatikoissa, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.MUUTOKSET_POYTALAATIKOISSA,
              },
              {
                arvo: this._currencyService.formatoiRaha(data.muutoksetTauoissa, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.MUUTOKSET_TAUOISSA,
              },
              {
                arvo: this._currencyService.formatoiRaha(data.hinnanmuutokset, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.HINNANMUUTOKSET,
              },
              {
                arvo: this._currencyService.formatoiRaha(data.sisaisetSiirrot, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.SISAISET_SIIRROT,
              },
              ...(naytaTagiSarake
                ? [
                    {
                      arvo: this._currencyService.formatoiRaha(data.muutoksetTagirajauksessa ?? 0, 'EUR', 'fi'),
                      kirjanpitajaAvain: data.kirjanpitaja,
                      kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.MUUTOKSET_TAGIRAJAUKSESSA,
                    },
                  ]
                : []),
              {
                arvo: this._currencyService.formatoiRaha(data.alkaneetSopimukset, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.ALKANEET_SOPIMUKSET,
              },
              {
                arvo: this._currencyService.formatoiRaha(data.jatkuvaLaskutusYhteensa, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.JATKUVA_LASKUTUS_YHTEENSA,
              },
              { arvo: this._currencyService.formatoiRaha(data.muutosEdelliseenKuukauteen, 'EUR', 'fi') },
              {
                arvo: this._currencyService.formatoiRaha(data.poistumaYhteensa, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.POISTUMA_YHTEENSA,
              },
              {
                arvo: this._currencyService.formatoiRaha(data.lisaysYhteensa, 'EUR', 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.LISAYS_YHTEENSA,
              },
            ],
            nimi,
            bold: false,
          }
          rivit.push(kirjanpitajaRivi)
        }

        if (vastaus.jatkuvanLaskutuksenKehitysYhteensa) {
          const yhteensaRivi = this._luoJatkuvanLaskutuksenKehityksenYhteensaRivi(vastaus.jatkuvanLaskutuksenKehitysYhteensa)
          rivit.push(yhteensaRivi)
        }

        return rivit
      }),
      tap(() => {
        this.jatkuvanLaskutuksenKehitysTableLataa.set(false)
      }),
    )

    this.tilastotObservable = tilastotJaNimitiedotObservable.pipe(
      map(([vastaus, nimitiedotMap]) => {
        const rivit: TilastotTablenRivi[] = []
        const otsikkoRivi: TilastotTablenRivi = {
          cols: [{ arvo: 'Tmi (palvelupaketti)' }, { arvo: 'Oy (palvelupaketti)' }, { arvo: 'Kasvu (palvelupaketti)' }, { arvo: 'Yhteensä' }, { arvo: 'Laskutus yhteensä' }, { arvo: 'Keskiveloitus' }],
          nimi: 'Kirjanpitäjä',
          bold: true,
        }
        rivit.push(otsikkoRivi)

        const sortattuData = this.sorttaaKirjanpitajatNimenMukaan(vastaus.tilastot, nimitiedotMap)

        for (const data of sortattuData) {
          const nimi = this.muodostaKirjanpitajanNimi(data.kirjanpitaja, nimitiedotMap)

          const kirjanpitajaRivi: TilastotTablenRivi = {
            cols: [
              {
                arvo: this._currencyService.formatoiDesimaali(data.tmiPalvelupaketti.yhteensa, 0, 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaTilastot: TiimaritTilastotKentta.TMI_PALVELUPAKETTI,
              },
              {
                arvo: this._currencyService.formatoiDesimaali(data.oyPalvelupaketti.yhteensa, 0, 'fi'),
                kirjanpitajaAvain: data.kirjanpitaja,
                kenttaTilastot: TiimaritTilastotKentta.OY_PALVELUPAKETTI,
              },
              { arvo: this._currencyService.formatoiDesimaali(data.kasvuPalvelupaketti, 0, 'fi'), kirjanpitajaAvain: data.kirjanpitaja, kenttaTilastot: TiimaritTilastotKentta.KASVU_PALVELUPAKETTI },
              { arvo: this._currencyService.formatoiDesimaali(data.yhteensa, 0, 'fi'), kirjanpitajaAvain: data.kirjanpitaja, kenttaTilastot: TiimaritTilastotKentta.YHTEENSA },
              { arvo: this._currencyService.formatoiRaha(data.laskutusYhteensa, 'EUR', 'fi'), kirjanpitajaAvain: data.kirjanpitaja, kenttaTilastot: TiimaritTilastotKentta.LASKUTUS_YHTEENSA },
              { arvo: this._currencyService.formatoiRaha(data.keskiveloitus, 'EUR', 'fi'), kirjanpitajaAvain: data.kirjanpitaja },
            ],
            nimi,
            bold: false,
          }
          rivit.push(kirjanpitajaRivi)
        }

        if (vastaus.tilastotYhteensa) {
          const yhteensaRivi = this._luoTilastotYhteensaRivi(vastaus.tilastotYhteensa)
          rivit.push(yhteensaRivi)
        }

        return rivit
      }),
      tap(() => {
        this.tilastotTableLataa.set(false)
      }),
    )
  }

  private muodostaKirjanpitajanNimi(kirjanpitajaAvain: string, nimitiedotMap: Map<string, KirjanpitajanNimitiedot> | null): string {
    if (!nimitiedotMap) {
      return kirjanpitajaAvain
    }
    const nimitiedot = nimitiedotMap.get(kirjanpitajaAvain)
    if (!nimitiedot) {
      return kirjanpitajaAvain
    }
    return nimitiedot.etunimi + ' ' + nimitiedot.sukunimi
  }

  private sorttaaKirjanpitajatNimenMukaan<T extends { kirjanpitaja: string }>(data: T[], nimitiedotMap: Map<string, KirjanpitajanNimitiedot> | null): T[] {
    if (!nimitiedotMap) {
      return [...data].sort((a, b) => a.kirjanpitaja.localeCompare(b.kirjanpitaja, 'fi'))
    }
    return [...data].sort((a, b) => {
      const nimiA = nimitiedotMap.get(a.kirjanpitaja)
      const nimiB = nimitiedotMap.get(b.kirjanpitaja)
      const etunimiA = nimiA?.etunimi || a.kirjanpitaja
      const etunimiB = nimiB?.etunimi || b.kirjanpitaja
      const etunimiVertailu = etunimiA.localeCompare(etunimiB, 'fi')
      if (etunimiVertailu !== 0) {
        return etunimiVertailu
      }
      const sukunimiA = nimiA?.sukunimi || ''
      const sukunimiB = nimiB?.sukunimi || ''
      return sukunimiA.localeCompare(sukunimiB, 'fi')
    })
  }

  chosenMonthHandler(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    asetaArvo(this.lomake.kuukausi, normalizedMonth)
    datepicker.close()
  }

  async avaaSolunTiedotMyyntiyhteenveto(solunData: TilastotSolunData) {
    if (!solunData || !solunData.kenttaMyyntiyhteenveto) {
      return
    }

    if (this._myyntiyhteenvetoTila().valittuSolu === solunData) {
      this._myyntiyhteenvetoTila.set(this._luoTyhjaMyyntiyhteenvetoTila())
      return
    }

    this._myyntiyhteenvetoTila.set({
      valittuSolu: solunData,
      lahteet: [],
      lahteetYhteissumma: 0,
      lataaLahteet: true,
      lahteetEiTasmaa: false,
    })

    const kuukausiDate = this.lomake.kuukausi().value()
    if (!kuukausiDate) {
      this._myyntiyhteenvetoTila.update(tila => ({ ...tila, lataaLahteet: false }))
      return
    }

    const kuukausi = this._dateService.dateToLocalDate(kuukausiDate)

    const tiimiValue = this.lomake.tiimi().value() ?? ''
    const pyynto: TiimaritTilastotPyynto = {
      aikavali: { start: { year: kuukausi.year, month: kuukausi.month }, end: { year: kuukausi.year, month: kuukausi.month } },
      tiimi: tiimiValue || null,
      kirjanpitaja: solunData.kirjanpitajaAvain ?? null,
      raportti: TiimaritRaporttiTyyppi.MYYNTIYHTEENVETO,
      palautaSolu: [solunData.kenttaMyyntiyhteenveto],
      rajoitettuTagiinAvain: this.lomake.tagi().value() ?? null,
    }

    try {
      const vastaus = await this._lemonatorFirestore.functionsCall<TiimaritTilastotPyynto, TiimaritTilastotVastaus>('tiimaritTilastot', pyynto)
      const asiakkaatMap = await this.asiakkaatMapPromise
      const lahteet: MyyntiYhteenvetoLahdeTietoNimitiedoilla[] = ((vastaus.lahteet ?? []) as MyyntiYhteenvetoLahdeTieto[]).map(l => ({
        ...l,
        asiakasNimi: asiakkaatMap?.[l.av]?.nimi ?? '',
      }))
      const yhteissumma = lahteet.reduce((sum, lahde) => sum + (lahde.s ?? 0), 0)
      const solunArvo = this._currencyService.muutaMerkkijonoNumeroksiKaikkiDesimaalit(solunData.arvo) ?? 0

      this._myyntiyhteenvetoTila.set({
        valittuSolu: solunData,
        lahteet,
        lahteetYhteissumma: yhteissumma,
        lataaLahteet: false,
        lahteetEiTasmaa: Math.abs(yhteissumma - solunArvo) > 0.01,
      })
    } catch (err) {
      this._myyntiyhteenvetoTila.set({
        valittuSolu: solunData,
        lahteet: [],
        lahteetYhteissumma: 0,
        lataaLahteet: false,
        lahteetEiTasmaa: false,
      })
    }
  }

  async avaaSolunTiedotJatkuvaLaskutus(solunData: TilastotSolunData) {
    if (!solunData || !solunData.kenttaJatkuvaLaskutus) {
      return
    }

    if (this._jatkuvanLaskutuksenKehitysTila().valittuSolu === solunData) {
      this._jatkuvanLaskutuksenKehitysTila.set(this._luoTyhjaJatkuvanLaskutuksenKehitysTila())
      return
    }

    this._jatkuvanLaskutuksenKehitysTila.set({
      valittuSolu: solunData,
      lahteet: [],
      lahteetYhteissumma: 0,
      lataaLahteet: true,
      lahteetEiTasmaa: false,
    })

    const kuukausiDate = this.lomake.kuukausi().value()
    if (!kuukausiDate) {
      this._jatkuvanLaskutuksenKehitysTila.update(tila => ({ ...tila, lataaLahteet: false }))
      return
    }

    const kuukausi = this._dateService.dateToLocalDate(kuukausiDate)

    const tiimiValue = this.lomake.tiimi().value() ?? ''
    const pyynto: TiimaritTilastotPyynto = {
      aikavali: { start: { year: kuukausi.year, month: kuukausi.month }, end: { year: kuukausi.year, month: kuukausi.month } },
      tiimi: tiimiValue || null,
      kirjanpitaja: solunData.kirjanpitajaAvain ?? null,
      raportti: TiimaritRaporttiTyyppi.JATKUVAN_LASKUTUKSEN_KEHITYS,
      palautaSolu: [solunData.kenttaJatkuvaLaskutus],
      rajoitettuTagiinAvain: this.lomake.tagi().value() ?? null,
    }

    try {
      const vastaus = await this._lemonatorFirestore.functionsCall<TiimaritTilastotPyynto, TiimaritTilastotVastaus>('tiimaritTilastot', pyynto)
      const lahteet = (vastaus.lahteet ?? []) as JatkuvanLaskutuksenLahdeTieto[]
      const yhteissumma = lahteet.reduce((sum, lahde) => sum + (lahde.summa ?? 0), 0)
      const solunArvo = this._currencyService.muutaMerkkijonoNumeroksiKaikkiDesimaalit(solunData.arvo) ?? 0

      this._jatkuvanLaskutuksenKehitysTila.set({
        valittuSolu: solunData,
        lahteet,
        lahteetYhteissumma: yhteissumma,
        lataaLahteet: false,
        lahteetEiTasmaa: Math.abs(yhteissumma - solunArvo) > 0.01,
      })
    } catch (err) {
      this._jatkuvanLaskutuksenKehitysTila.set({
        valittuSolu: solunData,
        lahteet: [],
        lahteetYhteissumma: 0,
        lataaLahteet: false,
        lahteetEiTasmaa: false,
      })
    }
  }

  async avaaSolunTiedotTilastot(solunData: TilastotSolunData) {
    if (!solunData || !solunData.kenttaTilastot) {
      return
    }

    if (this._tilastotTila().valittuSolu === solunData) {
      this._tilastotTila.set(this._luoTyhjaTilastotTila())
      return
    }

    this._tilastotTila.set({
      valittuSolu: solunData,
      lahteet: [],
      lahteetYhteissumma: 0,
      lataaLahteet: true,
      lahteetEiTasmaa: false,
    })

    const kuukausiDate = this.lomake.kuukausi().value()
    if (!kuukausiDate) {
      this._tilastotTila.update(tila => ({ ...tila, lataaLahteet: false }))
      return
    }

    const kuukausi = this._dateService.dateToLocalDate(kuukausiDate)

    const tiimiValue = this.lomake.tiimi().value() ?? ''
    const pyynto: TiimaritTilastotPyynto = {
      aikavali: { start: { year: kuukausi.year, month: kuukausi.month }, end: { year: kuukausi.year, month: kuukausi.month } },
      tiimi: tiimiValue || null,
      kirjanpitaja: solunData.kirjanpitajaAvain ?? null,
      raportti: TiimaritRaporttiTyyppi.TILASTOT,
      palautaSolu: [solunData.kenttaTilastot],
      rajoitettuTagiinAvain: this.lomake.tagi().value() ?? null,
    }

    try {
      const vastaus = await this._lemonatorFirestore.functionsCall<TiimaritTilastotPyynto, TiimaritTilastotVastaus>('tiimaritTilastot', pyynto)
      const asiakkaatMap = await this.asiakkaatMapPromise
      const lahteet: TilastotLahdeTietoNimitiedoilla[] = ((vastaus.lahteet ?? []) as TilastotLahdeTieto[]).map(l => ({
        ...l,
        asiakasNimi: asiakkaatMap?.[l.av]?.nimi ?? '',
      }))
      const yhteissumma = lahteet.reduce((sum, lahde) => sum + (lahde.summa ?? 0), 0)
      const solunArvo = this._currencyService.muutaMerkkijonoNumeroksiKaikkiDesimaalit(solunData.arvo) ?? 0

      const lahteetEiTasmaa = solunData.kenttaTilastot === TiimaritTilastotKentta.LASKUTUS_YHTEENSA ? Math.abs(yhteissumma - solunArvo) > 0.01 : lahteet.length !== solunArvo

      this._tilastotTila.set({
        valittuSolu: solunData,
        lahteet,
        lahteetYhteissumma: yhteissumma,
        lataaLahteet: false,
        lahteetEiTasmaa,
      })
    } catch (err) {
      this._tilastotTila.set({
        valittuSolu: solunData,
        lahteet: [],
        lahteetYhteissumma: 0,
        lataaLahteet: false,
        lahteetEiTasmaa: false,
      })
    }
  }

  private _luoMyyntiyhteenvedonYhteensaRivi(yhteensa: KirjanpitajanMyyntiyhteenvetoYhteensa): TilastotTablenRivi {
    return {
      cols: [
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.kuukausittainenPalvelumaksu, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.KUUKAUSITTAINEN_PALVELUMAKSU,
        },
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.jatkuvaluonteinenKertalaskutus, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.JATKUVALUONTEINEN_KERTALASKUTUS,
        },
        { arvo: this._currencyService.formatoiRaha(yhteensa.kertalaskutus, 'EUR', 'fi'), kirjanpitajaAvain: null, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.KERTALASKUTUS },
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.kertalaskutusYhteensa, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.KERTALASKUTUS_YHTEENSA,
        },
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.palvelulaskutusYhteensa, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.PALVELULASKUTUS_YHTEENSA,
        },
        { arvo: this._currencyService.formatoiRaha(yhteensa.palkkapalvelut, 'EUR', 'fi'), kirjanpitajaAvain: null, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.PALKKAPALVELUT },
        { arvo: this._currencyService.formatoiRaha(yhteensa.ohjelmisto, 'EUR', 'fi'), kirjanpitajaAvain: null, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.OHJELMISTO },
        { arvo: this._currencyService.formatoiRaha(yhteensa.muuLaskutus, 'EUR', 'fi'), kirjanpitajaAvain: null, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.MUU_LASKUTUS },
        { arvo: this._currencyService.formatoiRaha(yhteensa.yhteensa, 'EUR', 'fi'), kirjanpitajaAvain: null, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.YHTEENSA },
        { arvo: this._currencyService.formatoiRaha(yhteensa.hyvitykset, 'EUR', 'fi'), kirjanpitajaAvain: null, kenttaMyyntiyhteenveto: TiimaritMyyntiyhteenvetoKentta.HYVITYKSET },
      ],
      nimi: 'Yhteensä',
      bold: true,
    }
  }

  private _luoJatkuvanLaskutuksenKehityksenYhteensaRivi(yhteensa: KirjanpitajanJatkuvanLaskutuksenKehitysYhteensa): TilastotTablenRivi {
    return {
      cols: [
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.edellinenKkLoppu, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.EDELLINEN_KK_LOPPU,
        },
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.paattyneetSopimukset, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.PAATTYNEET_SOPIMUKSET,
        },
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.muutoksetPoytalaatikoissa, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.MUUTOKSET_POYTALAATIKOISSA,
        },
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.muutoksetTauoissa, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.MUUTOKSET_TAUOISSA,
        },
        { arvo: this._currencyService.formatoiRaha(yhteensa.hinnanmuutokset, 'EUR', 'fi'), kirjanpitajaAvain: null, kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.HINNANMUUTOKSET },
        { arvo: this._currencyService.formatoiRaha(yhteensa.sisaisetSiirrot, 'EUR', 'fi'), kirjanpitajaAvain: null, kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.SISAISET_SIIRROT },
        ...(yhteensa.muutoksetTagirajauksessa !== undefined
          ? [
              {
                arvo: this._currencyService.formatoiRaha(yhteensa.muutoksetTagirajauksessa, 'EUR', 'fi'),
                kirjanpitajaAvain: null,
                kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.MUUTOKSET_TAGIRAJAUKSESSA,
              },
            ]
          : []),
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.alkaneetSopimukset, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.ALKANEET_SOPIMUKSET,
        },
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.jatkuvaLaskutusYhteensa, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.JATKUVA_LASKUTUS_YHTEENSA,
        },
        { arvo: this._currencyService.formatoiRaha(yhteensa.muutosEdelliseenKuukauteen, 'EUR', 'fi') },
        {
          arvo: this._currencyService.formatoiRaha(yhteensa.poistumaYhteensa, 'EUR', 'fi'),
          kirjanpitajaAvain: null,
          kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.POISTUMA_YHTEENSA,
        },
        { arvo: this._currencyService.formatoiRaha(yhteensa.lisaysYhteensa, 'EUR', 'fi'), kirjanpitajaAvain: null, kenttaJatkuvaLaskutus: TiimaritJatkuvanLaskutuksenKehitysKentta.LISAYS_YHTEENSA },
      ],
      nimi: 'Yhteensä',
      bold: true,
    }
  }

  private _luoTilastotYhteensaRivi(yhteensa: KirjanpitajanTilastotiedotYhteensa): TilastotTablenRivi {
    return {
      cols: [
        { arvo: this._currencyService.formatoiDesimaali(yhteensa.tmiPalvelupaketti.yhteensa, 0, 'fi'), kirjanpitajaAvain: null, kenttaTilastot: TiimaritTilastotKentta.TMI_PALVELUPAKETTI },
        { arvo: this._currencyService.formatoiDesimaali(yhteensa.oyPalvelupaketti.yhteensa, 0, 'fi'), kirjanpitajaAvain: null, kenttaTilastot: TiimaritTilastotKentta.OY_PALVELUPAKETTI },
        { arvo: this._currencyService.formatoiDesimaali(yhteensa.kasvuPalvelupaketti, 0, 'fi'), kirjanpitajaAvain: null, kenttaTilastot: TiimaritTilastotKentta.KASVU_PALVELUPAKETTI },
        { arvo: this._currencyService.formatoiDesimaali(yhteensa.yhteensa, 0, 'fi'), kirjanpitajaAvain: null, kenttaTilastot: TiimaritTilastotKentta.YHTEENSA },
        { arvo: this._currencyService.formatoiRaha(yhteensa.laskutusYhteensa, 'EUR', 'fi'), kirjanpitajaAvain: null, kenttaTilastot: TiimaritTilastotKentta.LASKUTUS_YHTEENSA },
        { arvo: this._currencyService.formatoiRaha(yhteensa.keskiveloitus, 'EUR', 'fi'), kirjanpitajaAvain: null },
      ],
      nimi: 'Yhteensä',
      bold: true,
    }
  }

  private _luoTyhjaMyyntiyhteenvetoTila(): MyyntiyhteenvetoTila {
    return {
      valittuSolu: null,
      lahteet: [],
      lahteetYhteissumma: 0,
      lataaLahteet: false,
      lahteetEiTasmaa: false,
    }
  }

  private _luoTyhjaJatkuvanLaskutuksenKehitysTila(): JatkuvanLaskutuksenKehitysTila {
    return {
      valittuSolu: null,
      lahteet: [],
      lahteetYhteissumma: 0,
      lataaLahteet: false,
      lahteetEiTasmaa: false,
    }
  }

  private _luoTyhjaTilastotTila(): TilastotTila {
    return {
      valittuSolu: null,
      lahteet: [],
      lahteetYhteissumma: 0,
      lataaLahteet: false,
      lahteetEiTasmaa: false,
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
